/**
 * P-06 — the pure rules of a task row and the task page (pinned by
 * `src/utils/__tests__/taskRowModel.test.ts`).
 */
import { effectiveProgress } from '../timer/progressModel';

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The task page (D-58, T-14): every task opens here, never on the ticket page. */
export function taskPath(id: string): string {
  return `/tasks/${id}`;
}

/**
 * D-56 / P-10e (D-82) — a task's progress: its saved percent (the bar moved in
 * the ⏸ / ✓ windows), never below its status (0 / 25 / 50 / 75 / 100);
 * completed = 100. Without a saved percent it is the status's value.
 */
export function taskProgress(status: string | null | undefined, progressPercent?: number | null): number {
  return effectiveProgress(status, progressPercent);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** `YYYY-MM-DD` of a DATE the API sends (an ISO midnight keeps its date). */
function dayOf(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}/.test(value) && (value.length === 10 || /T00:00:00(\.0+)?Z$/.test(value))) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * D-62 — "באיחור N ימים": whole days past the due date while the task is not
 * done (completed or cancelled). A hint next to the date, never a status.
 * null when not late.
 */
export function overdueDays(
  due: string | null | undefined,
  task: { status?: string | null; cancelled_at?: string | null },
  today: Date = new Date(),
): number | null {
  if (!due || task.status === 'completed' || task.status === 'closed' || task.cancelled_at) return null;
  const d = dayOf(due);
  if (!d) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const t = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
  if (d >= t) return null;
  return Math.round((Date.parse(`${t}T00:00:00Z`) - Date.parse(`${d}T00:00:00Z`)) / DAY_MS);
}

/** The assignees' names, the owner first (`agents` of the API, else the owner's name). */
export function assigneeNames(task: { agents?: { id: string; name: string }[] | null; agent_name?: string | null; assigned_agent_id?: string | null }): string[] {
  const agents = task.agents ?? [];
  if (agents.length === 0) return task.agent_name ? [task.agent_name] : [];
  const owner = agents.find((a) => a.id === task.assigned_agent_id);
  return [...(owner ? [owner] : []), ...agents.filter((a) => a !== owner)].map((a) => a.name);
}

/** C-31 (clarity, N-06) — the assignees cell: the first two names, and how many more. */
export function assigneesSummary(names: readonly string[], max = 2): { shown: string; more: number } {
  return { shown: names.slice(0, max).join(', '), more: Math.max(0, names.length - max) };
}

/** C-18 (clarity, D-11) — "שלי": the task is assigned to this user (owner or one of the assignees). */
export function isAssignedTo(task: { agents?: { id: string }[] | null; assigned_agent_id?: string | null }, userId: string | null | undefined): boolean {
  if (!userId) return false;
  if (task.assigned_agent_id === userId) return true;
  return (task.agents ?? []).some((a) => a.id === userId);
}

/**
 * D-59 — the question's text: "דנה שויכה למשימה. לשייך אותה גם לאבן הדרך
 * 'פיתוח' ולפרויקט 'אתר חדש'?" (a task without a milestone: the project only).
 */
export function alsoAssignQuestionText(t: Translate, names: string[], milestone: string | null, project: string | null): string {
  const who = names.length > 0 ? names.join(', ') : t('taskAssign.theWorker');
  const count = Math.max(1, names.length);
  if (milestone && project) return t('taskAssign.alsoBoth', { who, milestone, project, count });
  if (milestone) return t('taskAssign.alsoMilestone', { who, milestone, count });
  return t('taskAssign.alsoProject', { who, project: project ?? '', count });
}

/** D-60 — the related tasks of a project task in the page's order, grouped. */
export interface RelatedGroup<T> {
  key: string;
  kind: 'same' | 'milestone' | 'direct' | 'customer';
  /** The milestone's name (same / milestone groups). */
  title: string | null;
  rows: T[];
}

export function groupRelatedTasks<T extends { group: 'same' | 'milestone' | 'direct' | 'customer'; milestone_id: string | null; milestone_name: string | null }>(rows: readonly T[]): RelatedGroup<T>[] {
  const out: RelatedGroup<T>[] = [];
  const byKey = new Map<string, RelatedGroup<T>>();
  const push = (key: string, kind: RelatedGroup<T>['kind'], title: string | null, row: T) => {
    let g = byKey.get(key);
    if (!g) {
      g = { key, kind, title, rows: [] };
      byKey.set(key, g);
      out.push(g);
    }
    g.rows.push(row);
  };
  // The server already orders them: same → other milestones (by order) → direct.
  for (const r of rows) {
    if (r.group === 'same') push('same', 'same', r.milestone_name, r);
    else if (r.group === 'milestone') push(`ms:${r.milestone_id}`, 'milestone', r.milestone_name, r);
    else if (r.group === 'direct') push('direct', 'direct', null, r);
    else push('customer', 'customer', null, r);
  }
  const rank = { same: 0, milestone: 1, direct: 2, customer: 3 } as const;
  return out.sort((a, b) => rank[a.kind] - rank[b.kind]);
}

/** EQ-001: the milestone name of a task row; /tickets sends `milestones[]`, the customer-page endpoint sends `milestone_name`. */
export function taskMilestoneName(task: { milestones?: { name: string }[] | null; milestone_name?: string | null }): string | null {
  return (task.milestones ?? [])[0]?.name ?? task.milestone_name ?? null;
}
